/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { Project } from "types/Project";
import { AssetId } from "@phonos/types";
import gatewayFetch from "utilities/gatewayFetch";
import { RecordingSessionId } from "types/RecordingSession";
import { TranscriptionService } from "constants/TranscriptionService";

export enum SPEAKER_DIARIZATION_CONFIG {
  Enabled = "enabled",
  Disabled = "disabled",
}

type AssetTranscriptionParams = {
  speaker_diarization: SPEAKER_DIARIZATION_CONFIG;
  transcription_service?: TranscriptionService;
};

export const parseTranscriptionService: () =>
  | TranscriptionService
  | undefined = () => {
  const paramString = window.location.search.substring(1);
  const params = new URLSearchParams(paramString);
  const transcriptionService = Object.fromEntries(
    params.entries()
  ).transcriptionService;

  if (
    Object.values(TranscriptionService).includes(
      transcriptionService as TranscriptionService
    )
  ) {
    console.log(
      `📝 =====> override transcriptionService: ${transcriptionService}`
    );
    return transcriptionService as TranscriptionService;
  } else {
    return undefined;
  }
};

export const startAssetTranscription = async ({
  project,
  assetId,
  speakerDiarizationConfig,
}: {
  project: Project;
  assetId: AssetId;
  speakerDiarizationConfig: SPEAKER_DIARIZATION_CONFIG;
}): Promise<Response> => {
  const transcriptionService = parseTranscriptionService();

  let params: AssetTranscriptionParams = {
    speaker_diarization: speakerDiarizationConfig,
  };
  if (transcriptionService) {
    params.transcription_service = transcriptionService;
  }

  return await gatewayFetch(
    `/api/v1/projects/${project.id}/tracks/${assetId}/transcribe`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
    { params }
  );
};

export const startRecordingSessionTranscription = ({
  project,
  recordingSessionId,
}: {
  project: Project;
  recordingSessionId: RecordingSessionId;
}): Promise<Response> => {
  const transcriptionService = parseTranscriptionService();

  let params;
  if (transcriptionService) {
    params = { transcription_service: transcriptionService };
  }

  return gatewayFetch(
    `/api/v1/projects/${project.id}/recording_sessions/${recordingSessionId}/transcribe`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
    { params }
  );
};

export const cancelAssetTranscription = async ({
  project,
  assetId,
}: {
  project: Project;
  assetId: AssetId;
}): Promise<Response> => {
  return await gatewayFetch(
    `/api/v1/projects/${project.id}/tracks/${assetId}/cancel_transcribe`,
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    }
  );
};
